import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "../utils/helpers";

export default function CicdWebhookSettings({ spaceId, canManage }) {
  const { t } = useTranslation("cicdmonitor");
  const write = useRef(null);
  useEffect(() => () => write.current?.abort(), [spaceId]);
  const [open, setOpen] = useState(false), [config, setConfig] = useState(null);
  const [error, setError] = useState(null), [busy, setBusy] = useState(false);
  useEffect(() => { setOpen(false); setConfig(null); setError(null); setBusy(false); }, [spaceId]);
  useEffect(() => {
    if (!open || !spaceId) return;
    const controller = new AbortController();
    setBusy(true); setError(null);
    api(`/api/cicd/events/config?spaceId=${encodeURIComponent(spaceId)}`, { signal: controller.signal })
      .then(value => { if (!controller.signal.aborted) setConfig(value); })
      .catch(() => { if (!controller.signal.aborted) setError(t("webhook.readError")); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [open, spaceId, t]);
  const prepare = async () => {
    if (write.current && !write.current.signal.aborted) return;
    const controller = new AbortController(); write.current = controller;
    setBusy(true); setError(null);
    try {
      const result = await api("/api/cicd/events/config", { method: "POST", signal: controller.signal, body: JSON.stringify({ spaceId }) });
      if (!controller.signal.aborted) setConfig(result);
    } catch { if (!controller.signal.aborted) setError(t("webhook.writeError")); }
    finally { if (!controller.signal.aborted) setBusy(false); if (write.current === controller) write.current = null; }
  };
  const close = () => { setOpen(false); setConfig(null); };
  const relativePath = typeof config?.webhookPath === "string" && /^\/api\/cicd\/webhooks\/[a-zA-Z0-9_-]+$/.test(config.webhookPath) ? config.webhookPath : null;
  const callbackUrl = config?.callbackUrl || (relativePath ? new URL(relativePath, window.location.origin).href : "");
  return <>
    <Button size="small" onClick={() => setOpen(true)} disabled={!spaceId}>{t("webhook.open")}</Button>
    <Dialog PaperProps={{ className: "iio" }} open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm" aria-labelledby="cicd-webhook-title">
      <DialogTitle id="cicd-webhook-title">{t("webhook.title")}</DialogTitle>
      <DialogContent>
        <Typography paragraph>{t("webhook.description")}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {config && <Box sx={{ display: "grid", gap: 2 }}>
          <Alert severity={config.lastDeliveryAt ? "success" : "info"}>{config.lastDeliveryAt
            ? t("webhook.received", { time: new Date(config.lastDeliveryAt).toLocaleString() }) : t("webhook.waiting")}</Alert>
          <TextField label={t("webhook.url")} value={callbackUrl} InputProps={{ readOnly: true }} fullWidth />
          <Typography>{t("webhook.instructions")}</Typography>
          {config.secret && <><Alert severity="warning">{t("webhook.secretOnce")}</Alert>
            <TextField label={t("webhook.secret")} value={config.secret} InputProps={{ readOnly: true }} fullWidth autoComplete="off" /></>}
          {!config.secret && canManage && config.canConfigure !== false && <>
            {config.configured && <Typography>{t("webhook.rotation")}</Typography>}
            <Button variant="outlined" disabled={busy} onClick={prepare}>{t(config.configured ? "webhook.rotate" : "webhook.prepare")}</Button>
          </>}
        </Box>}
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={close}>{t("webhook.close")}</Button></DialogActions>
    </Dialog>
  </>;
}
